import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import type { TrackEvent } from "@/types/generated";
import { Button } from "../ui/button";
import { router } from "@inertiajs/react";
import { toast } from "sonner";
import { FileUploader } from "../FileUploader";

interface EventFilesUploaderCardProps {
  trackEvent: TrackEvent;
}

export function EventFilesUploaderCard({
  trackEvent,
}: EventFilesUploaderCardProps) {
  const [selectedFiles, setSelectedFiles] = useState<{
    images: File[];
    videos: File[];
    setup_files: File[];
  }>({
    images: [],
    videos: [],
    setup_files: [],
  });

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | undefined>(
    undefined
  );

  if (!trackEvent.can_edit) return null;

  function handleFilesSelected(files: {
    images: File[];
    videos: File[];
    setup_files: File[];
  }) {
    setSelectedFiles(files);
  }

  function handleRemoveFile(
    type: "images" | "videos" | "setup_files",
    index: number
  ) {
    setSelectedFiles((prev) => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index),
    }));
  }

  function handleFileUpload() {
    if (!trackEvent.can_edit) {
      toast.error("Unauthorized", {
        description: "You can only upload files to your own events.",
      });
      return;
    }
    if (
      selectedFiles.images.length === 0 &&
      selectedFiles.videos.length === 0 &&
      selectedFiles.setup_files.length === 0
    ) {
      toast.error("No files selected", {
        description: "Please select at least one file to upload.",
      });
      return;
    }
    setIsUploading(true);
    const formData = {
      track_event: {
        images: selectedFiles.images,
        videos: selectedFiles.videos,
        setup_files: selectedFiles.setup_files,
      },
    };
    router.put(`/event/${trackEvent.id}`, formData, {
      forceFormData: true,
      preserveUrl: true,
      onProgress: (progress) => setUploadProgress(progress?.percentage),
      onSuccess: () => {
        setIsUploading(false);
        setUploadProgress(undefined);
        toast.success("Files uploaded!", {
          description: "Your files have been uploaded successfully.",
        });
        setSelectedFiles({ images: [], videos: [], setup_files: [] });
      },
      onError: (errors) => {
        setIsUploading(false);
        setUploadProgress(undefined);
        toast.error("Upload failed", {
          description: Object.values(errors).join(", "),
        });
      },
      onFinish: () => {
        setIsUploading(false);
        setUploadProgress(undefined);
      },
    });
  }

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-base">Upload Files</CardTitle>
        <CardDescription>
          Drag & drop or click to add images, videos, and setup files
        </CardDescription>
      </CardHeader>
      <CardContent>
        <FileUploader
          onFilesSelected={handleFilesSelected}
          selectedFiles={selectedFiles}
          onRemoveFile={handleRemoveFile}
          isUploading={isUploading}
          uploadProgress={uploadProgress}
        />
        {(selectedFiles.images.length > 0 ||
          selectedFiles.videos.length > 0 ||
          selectedFiles.setup_files.length > 0) && (
          <div className="mt-4">
            <Button
              onClick={handleFileUpload}
              disabled={isUploading}
              className="w-full"
            >
              {isUploading && uploadProgress
                ? `Uploading... ${uploadProgress}%`
                : "Upload Files"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
